import React, { useState, useRef, useEffect } from "react";
import { apiService } from "../services/api";
import styled from "styled-components";
import { X } from "react-feather";
import { WORKSPACE_UI_LABELS } from "../constants/workspaceUiLabels";
import { rawColors } from "../design-system/tokens/colors";

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.08);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Popup = styled.div`
  background: ${rawColors.structural.white};
  border-radius: 8px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.12);
  padding: 24px;
  min-width: 320px;
  max-width: 90vw;
  position: relative;
`;

const PopupTitle = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #222;
  font-family: inherit;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  // font-size: 22px;
  color: #888;
  cursor: pointer;
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: background 0.15s;
  &:hover {
    background: #f5f5f5;
    color: #222;
  }
`;

const Content = styled.div`
  // padding: 24px;
  display: flex;
  flex-direction: column;
  // gap: 16px;
`;

const Input = styled.input`
  width: -webkit-fill-available;
  padding: 8px 10px;
  margin-bottom: 12px;
  border: 1px solid ${rawColors.grey[200]};
  border-radius: 4px;
  font-size: 15px;
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 8px 10px;
  margin-bottom: 12px;
  border: 1px solid ${rawColors.grey[200]};
  border-radius: 4px;
  font-size: 15px;
  min-height: 60px;
  resize: vertical;
  width: -webkit-fill-available;
`;

const Button = styled.button`
  background: #1a73e8;
  color: ${rawColors.text.white};
  border: none;
  border-radius: 6px;
  padding: 10px 0;
  font-size: 16px;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;
  margin-top: 8px;
  transition: background 0.2s;
  &:hover {
    background: #155ab6;
  }
  &:disabled {
    background: #b3cdfa;
    cursor: not-allowed;
  }
`;

const ErrorMsg = styled.div`
  color: #e53935;
  font-size: 14px;
  margin-bottom: 4px;
`;

const PopupActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;

const PopupButton = styled.button`
  padding: 7px 18px;
  border-radius: 4px;
  border: none;
  font-weight: 500;
  font-size: 15px;
  background: #1a73e8;
  color: ${rawColors.text.white};
  cursor: pointer;
  &:hover {
    background: #1761c1;
  }
  &[data-variant="cancel"] {
    background: #f5f5f5;
    color: #222;
    border: 1px solid ${rawColors.grey[200]};
    &:hover {
      background: #e0e0e0;
    }
  }
`;

const WorkspacePopup: React.FC<{
  onSuccess: (ws: any) => void;
  onClose: () => void;
}> = ({ onSuccess, onClose }) => {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleCreate = async () => {
    setLoading(true);
    setError(null);
    try {
      const ws = await apiService.post("/api/v1/workspaces/", {
        name,
        description: desc,
      });
      onSuccess(ws);
      onClose();
    } catch (err: any) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Error creating workspace";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PopupOverlay>
      <Popup>
        <PopupTitle>{WORKSPACE_UI_LABELS.popupTitle}</PopupTitle>
        <CloseButton onClick={onClose}>
          <X size={22} />
        </CloseButton>
        <Content>
          <Input
            ref={inputRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={WORKSPACE_UI_LABELS.popupNamePlaceholder}
            autoFocus
          />
          <TextArea
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder={WORKSPACE_UI_LABELS.popupDescriptionPlaceholder}
          />
          {error && <ErrorMsg>{error}</ErrorMsg>}
          <PopupActions>
            <PopupButton data-variant="cancel" onClick={onClose}>
              Cancel
            </PopupButton>
            <PopupButton
              onClick={handleCreate}
              disabled={loading || !name.trim()}
            >
              {loading ? "Creating..." : "Create"}{" "}
            </PopupButton>
          </PopupActions>
        </Content>
      </Popup>
    </PopupOverlay>
  );
};

export default WorkspacePopup;
